import { useState } from 'react'

import { api } from '../api/client'
import type { Channel, EscalationPolicy, Page, Schedule, Team } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, humanDuration } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

interface StepDraft {
  position: number
  wait_seconds: number
  target_type: 'oncall' | 'team' | 'users'
  target_schedule_id: string | null
  target_team_id: string | null
  channel_ids: string[]
}

interface Draft {
  id?: string
  name: string
  description: string
  is_active: boolean
  repeat_times: number
  steps: StepDraft[]
}

const EMPTY_STEP: StepDraft = {
  position: 0,
  wait_seconds: 300,
  target_type: 'oncall',
  target_schedule_id: null,
  target_team_id: null,
  channel_ids: [],
}

export function EscalationPage() {
  const { can } = useAuth()
  const policies = useQuery(
    () => api.get<Page<EscalationPolicy>>('/escalation-policies', { limit: 200 }),
    [],
  )
  const teams = useQuery(() => api.get<Page<Team>>('/teams', { limit: 200 }), [])
  const schedules = useQuery(() => api.get<Page<Schedule>>('/schedules', { limit: 200 }), [])
  const channels = useQuery(() => api.get<Page<Channel>>('/channels', { limit: 200 }), [])
  const action = useAction()
  const [draft, setDraft] = useState<Draft | null>(null)

  const writable = can('policies.write')

  const save = async () => {
    if (!draft) return
    const body = {
      ...draft,
      steps: draft.steps.map((step, index) => ({ ...step, position: index })),
    }
    const ok = await action.run(() =>
      draft.id
        ? api.put(`/escalation-policies/${draft.id}`, body)
        : api.post('/escalation-policies', body),
    )
    if (ok) {
      setDraft(null)
      policies.reload()
    }
  }

  const remove = async (policy: EscalationPolicy) => {
    if (await action.run(() => api.del(`/escalation-policies/${policy.id}`))) policies.reload()
  }

  const name = (list: { id: string; name: string }[] | undefined, id: string | null) =>
    list?.find((item) => item.id === id)?.name ?? '—'

  return (
    <>
      <ErrorNote>{policies.error ?? action.error}</ErrorNote>

      <Card
        title="Политики эскалации"
        actions={
          writable && (
            <button
              className="btn-primary"
              onClick={() =>
                setDraft({
                  name: '',
                  description: '',
                  is_active: true,
                  repeat_times: 0,
                  steps: [{ ...EMPTY_STEP }],
                })
              }
            >
              Новая политика
            </button>
          )
        }
      >
        {policies.loading && !policies.data ? (
          <Spinner />
        ) : (policies.data?.items.length ?? 0) === 0 ? (
          <Empty>Политик нет. Без них инцидент никого не разбудит.</Empty>
        ) : (
          <div className="divide-y divide-ink-700">
            {policies.data!.items.map((policy) => (
              <div key={policy.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-100">
                      {policy.name}
                      {!policy.is_active && (
                        <span className="ml-2 text-xs text-slate-500">выключена</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {policy.description || 'без описания'} · повторов цикла:{' '}
                      {policy.repeat_times}
                    </div>
                  </div>
                  {writable && (
                    <div className="flex gap-1">
                      <button
                        className="btn-ghost text-xs"
                        onClick={() =>
                          setDraft({
                            id: policy.id,
                            name: policy.name,
                            description: policy.description,
                            is_active: policy.is_active,
                            repeat_times: policy.repeat_times,
                            steps: policy.steps.map((step) => ({
                              position: step.position,
                              wait_seconds: step.wait_seconds,
                              target_type: (step.target_type === 'webhook'
                                ? 'oncall'
                                : step.target_type) as StepDraft['target_type'],
                              target_schedule_id: step.target_schedule_id,
                              target_team_id: step.target_team_id,
                              channel_ids: step.channel_ids,
                            })),
                          })
                        }
                      >
                        Изменить
                      </button>
                      <button className="btn-danger text-xs" onClick={() => remove(policy)}>
                        Удалить
                      </button>
                    </div>
                  )}
                </div>

                <ol className="mt-3 space-y-1">
                  {policy.steps.map((step) => (
                    <li key={step.id} className="flex items-center gap-3 text-sm">
                      <span className="w-6 text-slate-500 tabular-nums">{step.position}</span>
                      <span className="text-slate-300">
                        {step.target_type === 'oncall'
                          ? `дежурный по «${name(schedules.data?.items, step.target_schedule_id)}»`
                          : step.target_type === 'team'
                            ? `команда «${name(teams.data?.items, step.target_team_id)}»`
                            : `${step.target_user_ids.length} чел.`}
                      </span>
                      <span className="text-slate-500">
                        каналы:{' '}
                        {step.channel_ids
                          .map((id) => name(channels.data?.items, id))
                          .join(', ') || 'по умолчанию у команды'}
                      </span>
                      <span className="ml-auto text-xs text-slate-500">
                        через {humanDuration(step.wait_seconds)} → следующий
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </Card>

      {draft && (
        <Card
          title={draft.id ? 'Изменение политики' : 'Новая политика'}
          actions={
            <>
              <button className="btn-ghost" onClick={() => setDraft(null)}>
                Отмена
              </button>
              <button className="btn-primary" disabled={action.pending} onClick={save}>
                Сохранить
              </button>
            </>
          }
        >
          <div className="p-4 space-y-4">
            <div className="grid lg:grid-cols-3 gap-4">
              <div>
                <label className="label">Название</label>
                <input
                  className="input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Описание</label>
                <input
                  className="input"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Повторов цикла</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={10}
                  value={draft.repeat_times}
                  onChange={(e) => setDraft({ ...draft, repeat_times: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="label">Шаги</div>
              {draft.steps.map((step, index) => (
                <div key={index} className="grid lg:grid-cols-5 gap-2 items-end bg-ink-900 p-3 rounded-md">
                  <div>
                    <label className="label">Кого уведомить</label>
                    <select
                      className="input"
                      value={step.target_type}
                      onChange={(e) => updateStep(index, { target_type: e.target.value as StepDraft['target_type'] })}
                    >
                      <option value="oncall">дежурного</option>
                      <option value="team">всю команду</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Расписание</label>
                    <select
                      className="input"
                      value={step.target_schedule_id ?? ''}
                      onChange={(e) => updateStep(index, { target_schedule_id: e.target.value || null })}
                      disabled={step.target_type !== 'oncall'}
                    >
                      <option value="">по команде инцидента</option>
                      {schedules.data?.items.map((schedule) => (
                        <option key={schedule.id} value={schedule.id}>
                          {schedule.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Команда</label>
                    <select
                      className="input"
                      value={step.target_team_id ?? ''}
                      onChange={(e) => updateStep(index, { target_team_id: e.target.value || null })}
                    >
                      <option value="">команда инцидента</option>
                      {teams.data?.items.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Пауза до следующего, сек</label>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      value={step.wait_seconds}
                      onChange={(e) => updateStep(index, { wait_seconds: Number(e.target.value) })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      className="input"
                      multiple
                      size={3}
                      value={step.channel_ids}
                      onChange={(e) =>
                        updateStep(index, {
                          channel_ids: Array.from(e.target.selectedOptions, (o) => o.value),
                        })
                      }
                    >
                      {channels.data?.items.map((channel) => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn-ghost self-start"
                      onClick={() =>
                        setDraft({ ...draft, steps: draft.steps.filter((_, i) => i !== index) })
                      }
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="btn-ghost text-xs"
                onClick={() => setDraft({ ...draft, steps: [...draft.steps, { ...EMPTY_STEP }] })}
              >
                Добавить шаг
              </button>
            </div>
          </div>
        </Card>
      )}
    </>
  )

  function updateStep(index: number, patch: Partial<StepDraft>) {
    if (!draft) return
    const steps = [...draft.steps]
    steps[index] = { ...steps[index], ...patch }
    setDraft({ ...draft, steps })
  }
}
